// mirrored from Core/Constants/WaterfallVocabularyDescriptions.cs — keep in lockstep
//
// The waterfall trigger-code catalog: the nine ServicingPaymentType codes
// (loan-scoped servicing / payoff runs) plus the event / engine trigger
// codes appearing in any seeded priority's fires-on set, with the backend's
// plain-language descriptions verbatim.

/** The nine ServicingPaymentType codes (Core/Entities/Payments/
 *  PaymentEngineConstants.cs), in canonical order. */
export const STANDARD_PAYMENT_TYPES = [
  "monthly_p_and_i",
  "balloon_payment",
  "bmsp_extension_monthly_payment",
  "full_voluntary_prepayment",
  "payoff",
  "late_fee_event",
  "nsf_event",
  "default_interest_accrual",
  "seller_make_whole",
] as const;

/** Every non-ServicingPaymentType trigger code in the seeded WF1/WF2
 *  fires-on sets: recovery, cost-recovery, fee/lifecycle, and
 *  contributed-side (WF2) inflow/closing events. */
export const EVENT_TRIGGER_CODES = [
  "collateral_liquidation_proceeds",
  "guarantor_payment",
  "insurance_proceeds",
  "inspection_audit_fee_recovery",
  "collection_enforcement_fee_recovery",
  "lender_cure_cost_recovery",
  "assumption_closing",
  "bmsp_extension_fee_payment",
  "right_size_closing",
  "amendment_waiver_fee_payment",
  "guarantor_release_fee_payment",
  "phoenix_backed_resale_closing",
  "vfi_inflow",
  "pmvfi_inflow",
  "investor_closing",
  "tail_closing",
  "subsequent_subscription",
] as const;

/** Plain-language description per trigger code — the backend catalog's
 *  strings, copied verbatim. */
export const TRIGGER_CODE_DESCRIPTIONS: Record<string, string> = {
  // The nine ServicingPaymentType values (loan-scoped servicing runs).
  monthly_p_and_i:
    "A regular monthly principal-and-interest payment from the borrower.",
  balloon_payment:
    "The balloon payment — the large final payment that retires the remaining balance at the " +
    "end of the loan term.",
  bmsp_extension_monthly_payment:
    "A monthly payment made while the loan is in a BMSP (balloon maturity) extension period.",
  full_voluntary_prepayment:
    "The borrower voluntarily pays the loan off early in full.",
  late_fee_event: "A late fee is collected on an overdue payment.",
  nsf_event:
    "A returned-payment (non-sufficient-funds) fee is collected after a bounced payment.",
  payoff:
    "The dedicated payoff run: settles all outstanding arrears and fees, charges the prepayment " +
    "admin fee and the yield-maintenance premium, returns the remaining principal to the " +
    "seller, and retires the loan.",
  default_interest_accrual:
    "An interim default-interest collection during a workout — the extra interest owed while the " +
    "loan is in default (not a scheduled P&I payment).",
  seller_make_whole:
    "The buyer's make-whole payment repaying default-interest amounts Phoenix previously took " +
    "ahead of the seller's income stream during a workout.",

  // Recovery events.
  collateral_liquidation_proceeds:
    "Money recovered by selling the loan's collateral after a default.",
  guarantor_payment: "A payment collected from a loan guarantor.",
  insurance_proceeds: "An insurance claim payout applied to the loan.",

  // Cost-recovery events (the borrower repays a cost Phoenix fronted).
  inspection_audit_fee_recovery:
    "The borrower repays an inspection or audit cost Phoenix fronted.",
  collection_enforcement_fee_recovery:
    "The borrower repays a collection or enforcement cost Phoenix fronted.",
  lender_cure_cost_recovery:
    "The borrower repays a lender-cure cost Phoenix fronted.",

  // Fee / lifecycle events.
  assumption_closing:
    "A loan assumption closes — a new borrower takes over the existing loan.",
  bmsp_extension_fee_payment:
    "The fee paid when a BMSP (balloon maturity) extension is granted.",
  right_size_closing:
    "A right-size closing completes — a negotiated reduction of the loan's principal.",
  amendment_waiver_fee_payment:
    "The fee paid for processing a loan amendment or waiver.",
  guarantor_release_fee_payment:
    "The fee paid for releasing a guarantor from the loan.",
  phoenix_backed_resale_closing:
    "A Phoenix-backed resale closes — the business is resold to a new buyer with Phoenix " +
    "coordinating the closing.",

  // Contributed-side (WF2) inflows and closings.
  vfi_inflow:
    "A Vendor Fee Income (VFI) inflow arrives to cascade through the contributed waterfall.",
  pmvfi_inflow:
    "A post-maturity VFI (PMVFI) recovery inflow arrives — vendor fee income collected after " +
    "maturity to cure investor shortfalls.",
  investor_closing:
    "An investor closing — investors fund the pool and contributed loan segments are purchased.",
  tail_closing:
    "A tail closing — a follow-on funding event after the pool's main investor closing.",
  subsequent_subscription:
    "A later investor subscription into the pool after its initial closing.",
};
